import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "../auth/AuthContext";
import { BusinessViewerViewModel } from "./business-viewer-view-model";
import { BusinessViewerProps } from "./business-viewer-props";
import UploadButton from "src/components/molecules/upload-button/upload-button";
import {
  BusinessModel,
  CPaaSProvider,
  IApproveBusinessRequest,
  IFileInfo,
  IBusinessNumber,
  ServiceStatus,
  IAttributeSection,
  IAttribute,
  IBusinessNumberApprovalRequest,
} from "src/types/models/business";
import { Download } from "@mui/icons-material";
import Logo from "src/components/molecules/logo/Logo";
import ButtonPro, {
  ButtonVariant,
} from "src/components/atoms/button-pro/button-pro";
import InputBox from "src/components/atoms/input-box/input-box";
import LoadingModal from "src/components/molecules/loading-modal/loading-modal";
import CheckBox from "src/components/atoms/checkbox/checkbox";

const BusinessViewer: React.FC<BusinessViewerProps> = ({
  businessId,
  onClose,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [business, setBusiness] = useState<BusinessModel>();
  const [comment, setComment] = useState("");
  const [supportEmailAddress, setSupportEmailAddress] = useState("");
  const [vonageApiKey, setVonageApiKey] = useState("");
  const [vonageApplicationId, setVonageApplicationId] = useState("");
  const [vonageApiSignatureSecret, setVonageApiSignatureSecret] = useState("");
  const [fileInfo, setFileInfo] = useState<IFileInfo>();
  const [selectedOutingNumbers, setSelectedOutingNumbers] = useState<string[]>(
    []
  );
  const [approvalStatusMessage, setApprovalStatusMessage] = useState("");
  const aStatuses: ServiceStatus[] = ["Active", "Approved"];

  const viewModelRef = useRef<BusinessViewerViewModel | null>(null);

  const fetchData = async () => {
    // console.log(
    //   `Open -> ${open}, businessId -> ${businessId}, user -> ${JSON.stringify(user)}`
    // );
    if (!businessId) {
      return;
    }
    setLoading(true);
    try {
      const response: BusinessModel | null | undefined =
        await viewModelRef.current?.fetchBusinessDetails(businessId);

      if (response) {
        setBusiness(response);
      }

      if (response && response.businessNumbers) {
        const activeNumbers = response.businessNumbers
          .filter((number: IBusinessNumber) =>
            aStatuses.includes(number.status)
          )
          .map((number: IBusinessNumber) => number.businessNumber);
        setSelectedOutingNumbers(activeNumbers);
      }
    } catch (error) {
      console.error("Error fetching business details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user || !user.accessToken || !businessId) {
      return;
    }

    if (!viewModelRef.current) {
      viewModelRef.current = new BusinessViewerViewModel(user.accessToken);
    }

    fetchData();
  }, []);

  const handleApproveNumber = async (
    businessId: string,
    number: string,
    pilotLimit?: number
  ) => {
    console.log(
      `number to approve ${number} pilot limit ${pilotLimit} for business id ${businessId}`
    );
    if (viewModelRef.current) {
      const request: IBusinessNumberApprovalRequest = {
        businessNumber: number,
        pilotSetupLimit: pilotLimit,
      };
      const result = await viewModelRef.current.submitBusinessNumberApproval(
        businessId,
        request
      );
      if (result) fetchData();
    }
  };

  const handleApprove = async () => {
    const approvalRequest: IApproveBusinessRequest = {
      businessId: businessId,
      businessNumbers: business?.businessNumbers.map((number) => ({
        businessNumber: number.businessNumber,
        pilotSetupLimit: number.pilotLimit,
      })),
      approvalDetails: {
        supportEmailAddress: supportEmailAddress,
        comments: comment,
        numberProviderConfig: {
          applicationId: vonageApplicationId,
          privateKeyFile: {
            fileName: fileInfo?.fileName || "",
            signedUrl: fileInfo?.signedUrl || "",
          },
          apiSignatureSecret: vonageApiSignatureSecret,
        },
        numberProvider: CPaaSProvider.Vonage,
      },
    };

    console.log(JSON.stringify(approvalRequest));

    const result =
      await viewModelRef.current?.submitBusinessApproval(approvalRequest);

    if (result) {
      await fetchData();
      setApprovalStatusMessage("Business has been approved...");
    } else {
      setApprovalStatusMessage("Failed to approve the Business");
    }

    console.log(`approval request result = ${result}`);
  };

  const handleReject = () => {
    console.log(`handle reject`);
  };

  const handleCancel = () => {
    if (onClose) {
      onClose();
    }
  };

  const handleSelect = (businessNumber: string) => {
    setSelectedOutingNumbers((prevSelected) =>
      prevSelected.includes(businessNumber)
        ? prevSelected.filter((number) => number !== businessNumber)
        : [...prevSelected, businessNumber]
    );
  };

  return (
    <>
      {loading && (
        <LoadingModal
          open={loading}
          message={`Loading data....`}
        ></LoadingModal>
      )}
      {/* main view */}
      <div className="font-Poppins bg-white p-6 min-w-96">
        {/* Business Information */}
        <div className=" flex flex-col items-center justify-center w-full">
          <div className="w-full text-center p-2 py-6 text-3xl font-Poppins font-semibold uppercase border-b-4">
            <Logo size={200} isCircular image={business?.logo.signedUrl}></Logo>
          </div>
          <div className="flex flex-col items-start justify-center gap-2 w-full p-6">
            <span className="font-Poppins font-semibold text-2xl uppercase tracking-wider">
              Business Information
            </span>
            <div className="flex flex-col items-start justify-center gap-6 pt-6 w-full">
              <InputBox
                label={"Business ID"}
                readOnly
                value={business?.businessId}
                type={"text"}
                onChange={function (value: string): void {
                  console.log(`do nothing. ${value}`);
                }}
              ></InputBox>
              <InputBox
                label={"Business Name"}
                readOnly
                value={business?.name}
                type={"text"}
                onChange={function (value: string): void {
                  console.log(`do nothing. ${value}`);
                }}
              ></InputBox>
              <InputBox
                label={"Website"}
                readOnly
                value={business?.website}
                type={"url"}
                onChange={function (value: string): void {
                  console.log(`do nothing. ${value}`);
                }}
              ></InputBox>
              <InputBox
                label={"Duns Number"}
                readOnly
                value={business?.dunsNumber ?? "NOT AVAILABLE"}
                type={"text"}
                onChange={function (value: string): void {
                  console.log(`do nothing. ${value}`);
                }}
              ></InputBox>
              <InputBox
                label={"Status"}
                readOnly
                value={business?.status}
                type={"text"}
                onChange={function (value: string): void {
                  console.log(`do nothing. ${value}`);
                }}
              ></InputBox>
              <InputBox
                label={"Country of Operations"}
                readOnly
                value={business?.countryCode}
                type={"text"}
                onChange={function (value: string): void {
                  console.log(`do nothing. ${value}`);
                }}
              ></InputBox>
            </div>
          </div>
          {/* Primary Contact */}
          {business && business.primaryContact && (
            <div className="flex flex-col items-start justify-center gap-2 w-full p-6">
              <span className="font-Poppins font-semibold text-2xl uppercase tracking-wider">
                Primary Contact
              </span>
              <div className="flex flex-col items-center justify-center gap-6 pt-6 w-full">
                <InputBox
                  label={"Name"}
                  readOnly
                  value={`${business.primaryContact.firstName || ""} ${business.primaryContact.lastName || ""}`.trim()}
                  type={"text"}
                  onChange={function (value: string): void {
                    console.log(`do nothing. ${value}`);
                  }}
                ></InputBox>
                <InputBox
                  label={"Email"}
                  readOnly
                  value={business.primaryContact.emailId}
                  type={"email"}
                  onChange={function (value: string): void {
                    console.log(`do nothing. ${value}`);
                  }}
                ></InputBox>
                {business.primaryContact.phoneNumber ? (
                  <InputBox
                    label={"Mobile No"}
                    readOnly
                    value={`+${business.primaryContact.phoneNumber.countryCode}${business.primaryContact.phoneNumber.number || ""}`.trim()}
                    type={"tel"}
                    onChange={function (value: string): void {
                      console.log(`do nothing. ${value}`);
                    }}
                  ></InputBox>
                ) : (
                  <span className="font-Poppins font-semibold flex items-center justify-start w-full text-red-500">
                    No phone number available
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Additional Information */}
        <div className="flex flex-col items-start justify-center gap-2 w-full p-6">
          {business?.attributeSections.map(
            (section: IAttributeSection, sectionIndex: number) => (
              <div
                key={sectionIndex}
                className="flex flex-col items-start justify-center w-full gap-6"
              >
                <span className="font-Poppins font-semibold text-2xl uppercase tracking-wider">
                  {section.sectionName}
                </span>
                <div className="flex flex-col items-start justify-start gap-6 w-full">
                  {section.attributes.map(
                    (attribute: IAttribute, attributeIndex: number) => (
                      <>
                        <InputBox
                          key={attributeIndex}
                          label={attribute.title}
                          value={attribute.value.value}
                          readOnly
                          type={"text"}
                          onChange={function (value: string): void {
                            console.log(`do nothing. ${value}`);
                          }}
                        ></InputBox>
                      </>
                    )
                  )}
                </div>
              </div>
            )
          )}
        </div>

        {/* Outgoing number */}
        <div className="flex flex-col items-start justify-center gap-2 w-full p-6">
          <span className="font-Poppins font-semibold text-2xl uppercase tracking-wider">
            Outgoing Numbers
          </span>
          <div className="overflow-x-auto w-full">
            <table className="min-w-full">
              <thead className="bg-slate-700">
                <tr className="font-Poppins text-center border-b">
                  <th className="px-4 py-2">
                    <CheckBox
                      onChange={(e) => {
                        if (e) {
                          setSelectedOutingNumbers(
                            business?.businessNumbers?.map(
                              (bn: any) => bn.businessNumber
                            ) || []
                          );
                        } else {
                          setSelectedOutingNumbers([]);
                        }
                      }}
                      checked={
                        selectedOutingNumbers.length ===
                        business?.businessNumbers.length
                      }
                    ></CheckBox>
                  </th>
                  <th className="px-4 py-2 border-l text-sm text-white">
                    <span className="font-Poppins font-semibold text-lg">
                      Business Number
                    </span>
                  </th>
                  <th className="px-4 py-2 border-l text-sm text-white">
                    <span className="font-Poppins font-semibold text-lg">
                      Status
                    </span>
                  </th>
                  <th className="px-4 py-2 border-l text-sm text-white text-center">
                    <span className="font-Poppins font-semibold text-lg">
                      Ownership Documents
                    </span>
                  </th>
                  <th className="px-4 py-2 border-l text-sm text-white text-center">
                    <span className="font-Poppins font-semibold text-lg">
                      {" "}
                      Pilot Limit
                    </span>
                  </th>
                  <th className="px-4 py-2 border-l text-sm text-white text-center">
                    <span className="font-Poppins font-semibold text-lg">
                      {" "}
                      Action
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {business?.businessNumbers.map((number: IBusinessNumber) => (
                  <tr
                    key={number.businessNumber}
                    className={`border-b border-gray-200 ${
                      selectedOutingNumbers.includes(number.businessNumber)
                        ? "bg-slate-300"
                        : ""
                    }`}
                  >
                    <td className="px-4 py-2">
                      <div className="flex items-center justify-center w-full">
                        <CheckBox
                          checked={selectedOutingNumbers.includes(
                            number.businessNumber
                          )}
                          onChange={() => handleSelect(number.businessNumber)}
                        ></CheckBox>
                      </div>
                    </td>
                    <td className="px-4 py-2 border-l text-sm text-white">
                      <span className="font-Poppins font-semibold text-lg text-black">
                        {number.businessNumber.startsWith("+")
                          ? number.businessNumber
                          : `+${number.businessNumber}`}
                      </span>
                    </td>
                    <td className="px-4 py-2 border-l text-sm text-white text-center">
                      <span className="font-Poppins font-semibold text-lg text-black">
                        {" "}
                        {number.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 border-l text-sm text-white text-center">
                      <span className="font-Poppins font-semibold text-lg text-black">
                        {number.ownershipDocuments.length > 0 ? (
                          number.ownershipDocuments.map(
                            (doc: any, index: number) => (
                              <a
                                key={index}
                                href={doc.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-500 underline"
                              >
                                <Download />
                              </a>
                            )
                          )
                        ) : (
                          <span>No documents available</span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-2 border-l text-sm text-white text-center">
                      {number.pilotLimit ? (
                        <span>{number.pilotLimit}</span>
                      ) : (
                        <select
                          className="bg-gray-700 text-white rounded p-2"
                          onChange={(e) =>
                            (number.pilotLimit = parseInt(e.target.value))
                          }
                        >
                          <option value="">Select Limit</option>
                          <option value="100">100</option>
                          <option value="200">200</option>
                          <option value="300">300</option>
                          <option value="400">400</option>
                          <option value="500">500</option>
                        </select>
                      )}
                    </td>
                    <td className="px-4 py-2 border-l text-sm text-white text-center">
                      {(business?.status == "Approved" ||
                        business?.status === "Active") &&
                      number.status === "New" ? (
                        <button
                          className={`${
                            !selectedOutingNumbers.includes(
                              number.businessNumber
                            )
                              ? "bg-green-200"
                              : "bg-green-600 hover:bg-green-700 text-white"
                          } text-black px-4 py-2 rounded font-Poppins font-medium`}
                          disabled={
                            !selectedOutingNumbers.includes(
                              number.businessNumber
                            )
                          }
                          onClick={() =>
                            handleApproveNumber(
                              business?.businessId,
                              number.businessNumber,
                              number.pilotLimit
                            )
                          }
                        >
                          Approve
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* action section */}
        <div className="flex flex-col items-start justify-items-center gap-4 p-6 w-full">
          <textarea
            className="font-Poppins tracking-wide w-full p-2 mb-2 text-md text-gray-900 border border-gray-600 rounded-md resize-y min-h-[20px]"
            rows={4}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Comments"
          />
          <InputBox
            value={supportEmailAddress}
            onChange={(value) => {
              setSupportEmailAddress(value);
            }}
            label="Support Email Address"
            type={"email"}
            required
          ></InputBox>
          <InputBox
            value={vonageApiKey}
            onChange={(value) => {
              setVonageApiKey(value);
            }}
            label="Vonage API Key"
            type={"text"}
          ></InputBox>
          <InputBox
            value={vonageApiSignatureSecret}
            onChange={(value) => {
              setVonageApiSignatureSecret(value);
            }}
            label="Vonage API Signature Secret"
            type={"password"}
          ></InputBox>
          <InputBox
            value={vonageApplicationId}
            onChange={(value) => {
              setVonageApplicationId(value);
            }}
            label="Vonage Application ID"
            type={"text"}
          ></InputBox>

          <label className="block text-sm text-white mb-1 text-wrap">
            Vonage private key - {fileInfo?.fileName}
          </label>
          <div className="flex flex-col gap-2">
            <UploadButton
              onUploadSuccess={function (file: IFileInfo): void {
                if (file && file.fileName) {
                  console.log(
                    `File uploaded with data ${file.fileName} ${file.signedUrl}`
                  );
                  setFileInfo(file);
                }
              }}
              disabled={aStatuses.includes(business?.status || "Active")}
            />
          </div>
          <div className="flex  items-start justify-items-center gap-4 mt-6 w-full">
            <ButtonPro label={"Approve"} onClick={handleApprove}></ButtonPro>
            <ButtonPro
              variant={ButtonVariant.Secondary}
              label={"Reject"}
              onClick={handleReject}
            ></ButtonPro>
            <ButtonPro
              variant={ButtonVariant.Secondary}
              label={"Cancel"}
              onClick={handleCancel}
            ></ButtonPro>
          </div>
        </div>
      </div>
    </>
  );
};

export default BusinessViewer;
